import { Injectable } from '@angular/core';
import { notificationError } from '@components/layout/error/error.utils';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ProductService } from '@service/product.service';
import { State } from '@store/state';
import { catchError, filter, map, of, switchMap, withLatestFrom } from 'rxjs';
import {
  loadChannelProducts, loadChannelProductsFailure, loadChannelProductsSuccess,
  loadChatGroupProducts, loadChatGroupProductsFailure, loadChatGroupProductsSuccess,
  loadChatSingleProducts, loadChatSingleProductsFailure, loadChatSingleProductsSuccess,
  loadEmailProducts, loadEmailProductsFailure, loadEmailProductsSuccess,
  loadPublicEmailProducts, loadPublicEmailProductsFailure, loadPublicEmailProductsSuccess,
  loadSmsProducts, loadSmsProductsFailure, loadSmsProductsSuccess,
} from './products.action';
import {
  getChannelProductsApiCall,
  getChatGroupProductsApiCall,
  getChatSingleProductsApiCall,
  getEmailProductsApiCall,
  getPublicEmailProductsApiCall,
  getSmsProductsApiCall,
} from './products.selector';

@Injectable()
export class ProductsEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly productService: ProductService,
    private readonly store: Store<State>
  ) {}

  loadEmailProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEmailProducts),
      withLatestFrom(this.store.select(getEmailProductsApiCall)),
      filter(([, apiCall]) => !apiCall.hasBeenCalled),
      switchMap(() =>
        this.productService.getEmailEventTypesByProductId().pipe(
          map((emailEventTypesByProductId) => loadEmailProductsSuccess({ emailEventTypesByProductId })),
          catchError((error) => of(loadEmailProductsFailure({ error: notificationError(error) })))
        )
      )
    )
  );

  loadPublicEmailProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPublicEmailProducts),
      withLatestFrom(this.store.select(getPublicEmailProductsApiCall)),
      filter(([, apiCall]) => !apiCall.hasBeenCalled),
      switchMap(() =>
        this.productService.getPublicEmailEventTypesByProductId().pipe(
          map((publicEmailEventTypesByProductId) => loadPublicEmailProductsSuccess({ publicEmailEventTypesByProductId })),
          catchError((error) => of(loadPublicEmailProductsFailure({ error: notificationError(error) })))
        )
      )
    )
  );

  loadSmsProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSmsProducts),
      withLatestFrom(this.store.select(getSmsProductsApiCall)),
      filter(([, apiCall]) => !apiCall.hasBeenCalled),
      switchMap(() =>
        this.productService.getSmsEventTypesByProductId().pipe(
          map((smsEventTypesByProductId) => loadSmsProductsSuccess({ smsEventTypesByProductId })),
          catchError((error) => of(loadSmsProductsFailure({ error: notificationError(error) })))
        )
      )
    )
  );

  loadChatSingleProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadChatSingleProducts),
      withLatestFrom(this.store.select(getChatSingleProductsApiCall)),
      filter(([, apiCall]) => !apiCall.hasBeenCalled),
      switchMap(() =>
        this.productService.getChatSingleCodesByProductId().pipe(
          map((chatSingleCodesByProductId) => loadChatSingleProductsSuccess({ chatSingleCodesByProductId })),
          catchError((error) => of(loadChatSingleProductsFailure({ error: notificationError(error) })))
        )
      )
    )
  );

  loadChatGroupProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadChatGroupProducts),
      withLatestFrom(this.store.select(getChatGroupProductsApiCall)),
      filter(([, apiCall]) => !apiCall.hasBeenCalled),
      switchMap(() =>
        this.productService.getChatGroupCodesByProductId().pipe(
          map((chatGroupCodesByProductId) => loadChatGroupProductsSuccess({ chatGroupCodesByProductId })),
          catchError((error) => of(loadChatGroupProductsFailure({ error: notificationError(error) })))
        )
      )
    )
  );

  loadChannelProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadChannelProducts),
      withLatestFrom(this.store.select(getChannelProductsApiCall)),
      filter(([, apiCall]) => !apiCall.hasBeenCalled),
      switchMap(() =>
        this.productService.getChannelProductIds().pipe(
          map((channelProductIds) => loadChannelProductsSuccess({ channelProductIds })),
          catchError((error) => of(loadChannelProductsFailure({ error: notificationError(error) })))
        )
      )
    )
  );
}
